import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Trophy, Target, ShieldCheck, TrendingUp, Users, FileCheck, Heart, Briefcase,
  AlertCircle, CheckCircle2, Sparkles, ArrowRight, Info, Building2, Globe, X,
  Pencil, Landmark,
} from "lucide-react";
import { sortRoadmap } from "../../lib/roadmapOrder";
import { api } from "../../lib/api";
import { LaunchPrepButton } from "../LaunchPrepButton";

/* =========================================================================
 * Web parity with the downloaded PDF — same sections, same data, plain
 * language so a non-finance founder can read it without a glossary.
 *
 * Used by:
 *   - /founder/readiness-report (authenticated founder)
 *   - /r/:token (public share view)
 *   - /sample-report (demo / marketing)
 *
 * Sections (mirrors the PDF):
 *   1. Score header (passed via parent — keeps customisation simple)
 *   2. What your score means    →  <BandPanel />
 *   3. Score breakdown          →  <PillarBreakdownPanel />
 *   4. Strengths & areas to improve  →  <StrengthsWeaknessesPanel />
 *   5. Capital structure        →  <CapitalStructurePanel />
 *   6. Funder shortlist         →  <FunderShortlistPanel />
 *   7. 90-day roadmap           →  <RoadmapPanel />
 *   8. Methodology              →  <MethodologyPanel />
 *
 * All copy is intentionally plain-English: short sentences, everyday words,
 * no jargon unless we immediately explain what it means.
 * ======================================================================= */

const PILLAR_META = [
  { key: "P1", label: "Financial Maturity",   plain: "How well you know your numbers", icon: TrendingUp },
  { key: "P2", label: "Traction & Market Proof", plain: "Real signs that customers want this", icon: Trophy },
  { key: "P3", label: "Market Clarity",       plain: "You understand who you sell to and why", icon: Target },
  { key: "P4", label: "Team Strength",        plain: "The people building this can pull it off", icon: Users },
  { key: "P5", label: "Structure & Compliance", plain: "Legals, tax, governance — funder-ready paperwork", icon: FileCheck },
  { key: "P6", label: "Impact / ESG",         plain: "Measurable good your business does for people / planet", icon: Heart },
];

const FUNDER_TYPE_LABEL = {
  angel_preseed: "Angel / Pre-Seed",
  seed_vc: "Seed / Pre-Series A VC",
  series_a_vc: "Series A VC",
  private_equity: "Growth / Private Equity",
  dfi_impact: "DFI Impact Window",
  commercial_bank: "Commercial Bank",
  revenue_based: "Revenue-Based Finance",
};

const FUNDER_TYPE_PLAIN = {
  angel_preseed: "individuals or syndicates who back very early ideas",
  seed_vc: "VC funds that back early start-ups with a working product",
  series_a_vc: "VCs investing once you have steady revenue growth",
  private_equity: "investors buying into more mature, profitable businesses",
  dfi_impact: "development finance institutions focused on social impact",
  commercial_bank: "banks giving loans against assets or cash flow",
  revenue_based: "non-dilutive funding repaid as a share of monthly revenue",
};

function _band(score) {
  if (score >= 85) return { label: "Capital-Ready", tone: "emerald",
    plain: "You're in great shape. Most institutional funders will take a serious look at you." };
  if (score >= 70) return { label: "Investment-Grade", tone: "teal",
    plain: "You're funder-ready. Expect real conversations and offers — keep tightening the basics." };
  if (score >= 55) return { label: "Promising", tone: "amber",
    plain: "You're close. Fix a few specific gaps and you'll start getting real meetings." };
  if (score >= 40) return { label: "Developing", tone: "orange",
    plain: "You have the bones of a fundable business. Get the documents and proof points in place first." };
  return { label: "Pre-Readiness", tone: "rose",
    plain: "You're early. Focus on building proof and putting the basics in writing — funding can come once you do." };
}

const _toneClasses = {
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", chip: "bg-emerald-100" },
  teal:    { bg: "bg-[#00828F]/10", text: "text-[#00828F]", border: "border-[#00828F]/20", chip: "bg-[#00828F]/15" },
  amber:   { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", chip: "bg-amber-100" },
  orange:  { bg: "bg-orange-50", text: "text-orange-800", border: "border-orange-200", chip: "bg-orange-100" },
  rose:    { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", chip: "bg-rose-100" },
};


/* ----------------------- 1b. Executive Summary ----------------- */
/** Mirrors PDF Section 2 — 5 blocks: business overview, CRS-in-context,
 *  capital recommendation, primary funder match, next action callout. */
export function ExecutiveSummaryPanel({ data, score }) {
  const company = data?.company || {};
  const analysis = data || {};
  const band = useMemo(() => _band(score || 0), [score]);
  const cls = _toneClasses[band.tone];
  const strengths = analysis.strengths || [];
  const weaknesses = analysis.weaknesses || [];
  const matches = analysis.funder_match_list || [];
  const top = matches[0];
  const rec = analysis.recommended_structure || {};
  const roadmap = sortRoadmap(analysis.roadmap || []);
  const nextAction = roadmap[0]
    ? `${roadmap[0].timeframe || "Week 1"}: ${roadmap[0].milestone || ""}${roadmap[0].capital_impact ? ` — this ${roadmap[0].capital_impact}` : ""}`
    : (weaknesses[0] ? `Address: ${weaknesses[0]}` : "");

  const businessText = analysis.summary
    || company.value_prop
    || `${company.company_name || "This business"} operates in ${company.sector || "an undisclosed sector"} in ${company.country || "Africa"}.`;

  return (
    <section
      data-testid="web-executive-summary"
      className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        Section 2
      </p>
      <h2 className="mt-1 text-2xl font-extrabold text-neutral-900">Executive Summary</h2>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">Business</p>
          <p className="mt-1.5 text-sm leading-relaxed text-neutral-800">{businessText}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">CRS in context</p>
          <p className={"mt-1.5 text-sm leading-relaxed " + cls.text}>
            <b>{score}/100</b> — {band.label} band.
          </p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-700">
            {strengths[0] && <>Strongest signal: <i>{strengths[0]}</i>. </>}
            {weaknesses[0] && <>Primary drag: <i>{weaknesses[0]}</i>.</>}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">Capital recommendation</p>
          <p className="mt-1.5 text-sm font-bold text-neutral-900">
            Primary: {(CAPITAL_TYPE_PLAIN[(rec.primary_type || "").toLowerCase()] || {}).title || rec.primary_type || "—"}
            {rec.secondary_type && <> · Secondary: {(CAPITAL_TYPE_PLAIN[rec.secondary_type.toLowerCase()] || {}).title || rec.secondary_type}</>}
          </p>
          {rec.rationale && (
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{rec.rationale}</p>
          )}
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">Primary funder match</p>
          {top ? (
            <>
              <p className="mt-1.5 text-sm font-bold text-neutral-900">
                {top.funder_category} · fit {top.fit_score ?? 0}/100
              </p>
              {top.match_reasons?.length > 0 && (
                <p className="mt-1 text-sm leading-relaxed text-neutral-700">
                  Why you match: {top.match_reasons.slice(0, 2).join(" · ")}
                </p>
              )}
            </>
          ) : (
            <p className="mt-1.5 text-sm text-neutral-500">No funder categories matched yet.</p>
          )}
        </div>
      </div>

      {nextAction && (
        <div
          data-testid="web-executive-next-action"
          className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
        >
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Next action</p>
          <p className="mt-1 text-sm font-bold leading-relaxed text-neutral-900">{nextAction}</p>
        </div>
      )}
    </section>
  );
}


/* ----------------------- Band ladder (visual) ----------------------- */
const _BAND_LADDER = [
  { min: 85, label: "Capital-Ready",     tone: "emerald" },
  { min: 70, label: "Investment-Grade",  tone: "teal" },
  { min: 55, label: "Promising",         tone: "amber" },
  { min: 40, label: "Developing",        tone: "orange" },
  { min: 0,  label: "Pre-Readiness",     tone: "rose" },
];

function BandLadder({ score }) {
  return (
    <div data-testid="web-band-ladder" className="mt-4 space-y-1.5">
      {_BAND_LADDER.map((b) => {
        const isHere = score >= b.min && (b === _BAND_LADDER[0] || score < _BAND_LADDER[_BAND_LADDER.indexOf(b) - 1].min);
        const cls = _toneClasses[b.tone];
        return (
          <div
            key={b.label}
            data-testid={`web-band-ladder-${b.label.toLowerCase()}`}
            data-active={isHere ? "1" : "0"}
            className={
              "flex items-center gap-3 rounded-lg border px-3 py-2 text-xs " +
              (isHere
                ? `${cls.border} ${cls.bg} ${cls.text} font-bold ring-2 ring-current/30`
                : "border-neutral-100 bg-white text-neutral-500")
            }
          >
            <span className="font-mono text-[11px] w-12 shrink-0">
              {b.min === 0 ? "0–39" : b.min === 85 ? "85+" : `${b.min}–${_BAND_LADDER[_BAND_LADDER.indexOf(b) - 1].min - 1}`}
            </span>
            <span className="flex-1">{b.label}</span>
            {isHere && <span className="text-[10px] font-bold uppercase tracking-wider">← You</span>}
          </div>
        );
      })}
    </div>
  );
}


/* ----------------------- 2. What your score means ----------------------- */
export function BandPanel({ score, summary, contextLine }) {
  const band = useMemo(() => _band(score || 0), [score]);
  const cls = _toneClasses[band.tone];
  return (
    <section className={`rounded-2xl border ${cls.border} ${cls.bg} p-5 sm:p-6`}
             data-testid="web-band-panel">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        What this score means
      </p>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-3">
        <h2 className={`text-2xl font-extrabold ${cls.text}`}>{band.label}</h2>
        <span className="text-sm font-semibold text-neutral-700">({score}/100)</span>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-neutral-700">{band.plain}</p>
      {summary && (
        <p className="mt-3 rounded-lg bg-white/70 p-3 text-sm leading-relaxed text-neutral-700">
          <span className="font-bold text-neutral-900">Summary: </span>{summary}
        </p>
      )}
      {contextLine && (
        <p className="mt-2 text-sm font-semibold text-[#0A8B85]" data-testid="web-score-context-line">
          {contextLine}
        </p>
      )}
      <BandLadder score={score || 0} />
    </section>
  );
}


/* ----------------------- 3. Score breakdown ----------------------- */
export function PillarBreakdownPanel({ pillarScores, pillarWeights, funderTypeUsed, totalScore, blendNote, contextLine, scoreBasis, pillarLabels, repayment }) {
  const rows = useMemo(() => PILLAR_META.map((p) => {
    const raw = Number(pillarScores?.[p.key] || 0);
    const weight = Number(pillarWeights?.[p.key] || 0);
    const label = pillarLabels?.[p.key] || p.label;
    const plain = pillarLabels?.[p.key] === "Repayment Capacity"
      ? "Can your cash flow comfortably repay the debt you're asking for" : p.plain;
    return { ...p, label, plain, raw, weight, contribution: Math.round(raw * weight) };
  }), [pillarScores, pillarWeights, pillarLabels]);

  const ftLabel = FUNDER_TYPE_LABEL[funderTypeUsed] || "Seed / Pre-Series A VC";
  const ftPlain = FUNDER_TYPE_PLAIN[funderTypeUsed] || FUNDER_TYPE_PLAIN.seed_vc;

  // First-visit nudge: highlight the weakest pillar (the one with the lowest
  // raw score) so a founder who just unlocked their report knows where to
  // start. Dismissed permanently on click or via the × inside the badge.
  const weakestKey = useMemo(() => {
    if (!rows.length) return null;
    const sorted = [...rows].sort((a, b) => a.raw - b.raw);
    return sorted[0]?.raw < 70 ? sorted[0].key : null;
  }, [rows]);
  const [startHereDismissed, setStartHereDismissed] = useState(() => {
    try { return localStorage.getItem("clinq_start_here_dismissed_v1") === "1"; }
    catch (_) { return false; }
  });
  const showStartHere = !!weakestKey && !startHereDismissed;
  const dismissStartHere = () => {
    setStartHereDismissed(true);
    try { localStorage.setItem("clinq_start_here_dismissed_v1", "1"); }
    catch (_) { /* ignore */ }
  };

  if (rows.every((r) => r.raw === 0 && r.weight === 0)) return null;

  return (
    <section id="score-breakdown" className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6 scroll-mt-24"
             data-testid="web-pillar-breakdown">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        How your score is made up
      </p>
      <h2 className="mt-1 text-2xl font-extrabold text-neutral-900">Score breakdown</h2>
      <p className="mt-2 text-sm leading-relaxed text-neutral-600" data-testid="web-score-basis">
        Your score is the weighted average of six pillars, scored for{" "}
        <b className="text-neutral-900">{scoreBasis?.label || `your selected funder type (${ftLabel})`}</b>
        {!scoreBasis?.label && <> ({ftPlain})</>} — some pillars matter more than others, so they carry higher weights below.
      </p>
      {repayment && (
        <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700" data-testid="web-repayment-capacity">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">Repayment capacity (P1) — how it was built</p>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ["Bank half", `${repayment.bank_score}/100`],
              ["Accounts half", `${repayment.accounts_score}/100`],
              ["Bank history", repayment.bank_months ? `${repayment.bank_months} months · ${repayment.bank_files || 1} file(s)` : "—"],
              ["Avg monthly inflows", repayment.avg_monthly_inflow_kes ? `KES ${Math.round(repayment.avg_monthly_inflow_kes / 1000).toLocaleString()}K` : "—"],
              [repayment.cfads_basis || "Cash flow", repayment.cfads_annual != null ? `${Math.round(repayment.cfads_annual / 1e6 * 10) / 10}M / yr` : "—"],
              ["Historical DSCR", repayment.dscr_historical != null ? `${repayment.dscr_historical.toFixed(2)}×` : "n/a (no existing debt)"],
              ["DSCR after new loan", repayment.dscr_after_new_loan != null ? `${repayment.dscr_after_new_loan.toFixed(2)}×` : "—"],
              ["Facility tested", repayment.proposed_loan ? `${Math.round(repayment.proposed_loan / 1e6 * 10) / 10}M` : "—"],
            ].map(([k, v]) => (
              <div key={k}><p className="text-[10px] uppercase tracking-wider text-neutral-500">{k}</p><p className="font-semibold text-neutral-900">{v}</p></div>
            ))}
          </div>
        </div>
      )}
      {scoreBasis?.recommended_score != null && (
        <p className="mt-2 rounded-lg border border-[#0A8B85]/30 bg-[#0A8B85]/5 px-3 py-2 text-sm text-neutral-800"
           data-testid="web-score-recommended-route">
          Scored for your <b>recommended route</b> ({scoreBasis.recommended_funder_label}):{" "}
          <b className="text-[#0A8B85]">{scoreBasis.recommended_score}/100</b> — same pillar scores, that funder type&apos;s weights.
        </p>
      )}
      {blendNote && (
        <p className="mt-2 text-sm font-semibold text-[#0A8B85]" data-testid="web-pillar-blend-note">
          {blendNote}
        </p>
      )}

      <div className="mt-5 overflow-hidden rounded-xl border border-neutral-100">
        <div className="grid grid-cols-12 gap-2 bg-neutral-50 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          <div className="col-span-6">Pillar</div>
          <div className="col-span-2 text-right">Your score</div>
          <div className="col-span-2 text-right">Weight</div>
          <div className="col-span-2 text-right">Contribution</div>
        </div>
        {rows.map((r, idx) => {
          const Icon = r.icon;
          const barPct = Math.max(2, r.raw);
          const barColor = r.raw >= 70 ? "bg-emerald-500"
                          : r.raw >= 50 ? "bg-[#004B62]"
                          : r.raw >= 30 ? "bg-amber-500" : "bg-rose-500";
          const isStartHere = showStartHere && r.key === weakestKey;
          return (
            <div
              key={r.key}
              id={`pillar-row-${r.key}`}
              data-testid={`web-pillar-${r.key}`}
              data-start-here={isStartHere ? "1" : undefined}
              onClick={isStartHere ? dismissStartHere : undefined}
              className={
                "relative grid grid-cols-12 items-center gap-2 px-4 py-3 transition-colors scroll-mt-24 " +
                (idx % 2 ? "bg-neutral-50/40" : "") +
                (isStartHere
                  ? " cursor-pointer ring-2 ring-amber-400 ring-offset-1 ring-offset-white bg-amber-50/60 motion-safe:animate-[pulse_1.6s_ease-in-out_infinite] hover:bg-amber-50"
                  : "")
              }
            >
              {isStartHere && (
                <span
                  data-testid="web-pillar-start-here-badge"
                  className="absolute -top-2.5 left-3 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-neutral-950 shadow-sm motion-safe:animate-none"
                >
                  <Sparkles className="h-3 w-3" />
                  Start here
                  <button
                    onClick={(e) => { e.stopPropagation(); dismissStartHere(); }}
                    aria-label="Dismiss start here"
                    data-testid="web-pillar-start-here-dismiss"
                    className="ml-0.5 -mr-0.5 rounded-full hover:bg-amber-500/30"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              <div className="col-span-6 flex items-center gap-2.5">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#00828F]/10 text-[#00828F]">
                  <Icon className="h-3.5 w-3.5"/>
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-neutral-900">{r.label}</p>
                  <p className="truncate text-[11px] text-neutral-500">
                    {r.key === "P6" && r.weight === 0
                      ? "Not applicable for your funder type and capital use"
                      : r.plain}
                  </p>
                </div>
              </div>
              <div className="col-span-2 text-right">
                <div className="font-mono text-sm font-bold text-neutral-900">{r.raw}<span className="text-neutral-400">/100</span></div>
                <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-neutral-200">
                  <div className={`h-full ${barColor}`} style={{ width: `${barPct}%` }}/>
                </div>
              </div>
              <div className="col-span-2 text-right font-mono text-sm font-bold text-neutral-700">
                {Math.round(r.weight * 100)}%
              </div>
              <div className="col-span-2 text-right font-mono text-sm font-bold text-[#00828F]">
                +{r.contribution}
              </div>
            </div>
          );
        })}
        <div className="grid grid-cols-12 gap-2 border-t border-neutral-200 bg-neutral-50 px-4 py-2.5 text-sm font-bold">
          <div className="col-span-8 text-neutral-700">Overall Capital Readiness Score</div>
          <div className="col-span-2"/>
          <div className="col-span-2 text-right text-[#00828F]">{totalScore} / 100</div>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-neutral-500" data-testid="web-pillar-rounding-note">
        Weights are displayed as rounded figures. The underlying score uses precise values.
      </p>
      {showStartHere && (
        <p
          data-testid="web-pillar-start-here-hint"
          className="mt-3 text-xs leading-relaxed text-neutral-600"
        >
          <Sparkles className="mr-1 inline h-3 w-3 text-amber-600" />
          The highlighted row is your weakest pillar — fixing this drags your
          overall score up the fastest. The roadmap below tells you exactly how.
        </p>
      )}
    </section>
  );
}


/* ----------------------- 4. Strengths & areas to improve ----------------- */
function _weaknessSeverity(idx, score) {
  // Mirrors PDF `_severity_for_index`: first 1-2 weaknesses are highest
  // severity, scaled down for high-scoring businesses.
  if (idx === 0) return score < 55
    ? { label: "Critical", color: "rose", icon: AlertCircle }
    : { label: "High",     color: "amber", icon: AlertCircle };
  if (idx === 1) return { label: "High",   color: "amber", icon: AlertCircle };
  if (idx === 2) return { label: "Medium", color: "neutral", icon: AlertCircle };
  return { label: "Low", color: "neutral", icon: AlertCircle };
}

const _SEV_PILL = {
  rose:    "bg-rose-100 text-rose-700",
  amber:   "bg-amber-100 text-amber-800",
  neutral: "bg-neutral-200 text-neutral-700",
};

const PILLAR_SHORT = { P1: "Financial", P2: "Traction", P3: "Market", P4: "Team", P5: "Compliance", P6: "Impact" };
const PillarTag = ({ pillar }) => pillar ? (
  <span className="mr-2 inline-block rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-600">
    {pillar} · {PILLAR_SHORT[pillar] || ""}
  </span>
) : null;

export function StrengthsWeaknessesPanel({ strengths, weaknesses, score, findings }) {
  if ((!strengths || strengths.length === 0) && (!weaknesses || weaknesses.length === 0)) return null;
  const pillarOf = (kind, text) => (findings?.[kind] || []).find((f) => f?.text === text)?.pillar;
  return (
    <section className="grid gap-5 md:grid-cols-2" data-testid="web-strengths-weaknesses">
      {strengths && strengths.length > 0 && (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-5 sm:p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
            What&apos;s working
          </p>
          <h2 className="mt-1 text-xl font-extrabold text-neutral-900">Your strengths</h2>
          <p className="mt-1 text-xs text-neutral-600">Show these off when you pitch — they&apos;re your unfair advantage.</p>
          <ul className="mt-4 space-y-3" data-testid="web-strengths-list">
            {strengths.map((s, i) => (
              <li key={`s-${i}`} className="flex gap-2.5 text-sm leading-relaxed text-neutral-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600"/>
                <span><PillarTag pillar={pillarOf("strengths", s)} />{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {weaknesses && weaknesses.length > 0 && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-5 sm:p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
            Where to focus next
          </p>
          <h2 className="mt-1 text-xl font-extrabold text-neutral-900">Areas to improve</h2>
          <p className="mt-1 text-xs text-neutral-600">Fix these and your score will move — funders look for these too.</p>
          <ul className="mt-4 space-y-3" data-testid="web-weaknesses-list">
            {weaknesses.map((w, i) => {
              const sev = _weaknessSeverity(i, score || 0);
              return (
                <li key={`w-${i}`} className="flex gap-2.5 text-sm leading-relaxed text-neutral-800">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600"/>
                  <div className="flex-1">
                    <span
                      data-testid={`web-weakness-severity-${i}`}
                      className={"mr-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " + _SEV_PILL[sev.color]}
                    >
                      {sev.label}
                    </span>
                    <PillarTag pillar={pillarOf("weaknesses", w)} />
                    <span>{w}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}


/* ----------------------- 5. Capital structure ----------------- */
const CAPITAL_TYPE_PLAIN = {
  grant: { title: "Grants",
           plain: "Free money — usually from foundations, governments or impact funds. No repayment, no equity given up." },
  equity: { title: "Equity",
            plain: "Selling a slice of your company for cash. Best for high-growth businesses willing to share ownership." },
  debt: { title: "Debt / Loans",
          plain: "Borrow money and repay with interest. Good if you have predictable revenue and assets." },
  revenue_based_finance: { title: "Revenue-Based Finance",
                          plain: "You repay a small share of monthly revenue until a fixed cap is hit. No equity given away." },
  trade_finance: { title: "Trade Finance",
                   plain: "Short-term funding to fulfil customer orders. Needs purchase orders or off-take agreements." },
  asset_finance: { title: "Asset Finance",
                   plain: "Borrow specifically against equipment / vehicles you need. The asset itself is collateral." },
  blended_finance: { title: "Blended Finance",
                     plain: "A mix of grants + commercial money — common for impact businesses that need de-risking." },
};

/* Per-capital-type unlock playbook. Each entry says: "Here are the concrete
 * things to do — and to upload — to flip this capital type from ❌ to ✅."
 * Steps are deliberately tactical (an action a founder can finish this week)
 * rather than aspirational. The matching `clinq_service_key` lets us deep-
 * link straight into a Clinq Services request when the founder wants help.
 */
const CAPITAL_TYPE_UNLOCK = {
  debt: {
    steps: [
      { text: "Engage a CPA to produce 2 years of audited financial statements (FY-1 + FY-2).", needs: ["audited_accounts"] },
      { text: "Renew / upload a current tax-compliance certificate (must not be expired).",     needs: ["tax_compliance"] },
      { text: "Add a full Cash Flow statement to your financial model — needed to compute DSCR.", needs: ["financial_model"] },
      { text: "Upload 6 months of bank statements as supporting evidence of cash flow.",         needs: ["bank_statements"] },
    ],
    clinq_service_key: "audited_accounts",
  },
  trade_finance: {
    steps: [
      { text: "Upload signed purchase orders OR off-take agreements with named buyers.", needs: ["purchase_orders"] },
      { text: "Add buyer credit references (or invoice history) showing they pay on time.", needs: ["mgmt_accounts"] },
      { text: "Document your end-to-end fulfilment timeline (PO → delivery → payment).", needs: [] },
    ],
    clinq_service_key: "investor_introductions",
  },
  asset_finance: {
    steps: [
      { text: "Upload the spec sheet + proforma invoice for the equipment / vehicle.", needs: ["asset_schedules"] },
      { text: "Add an asset schedule showing what's already on your balance sheet.",   needs: ["asset_schedules"] },
      { text: "Provide insurance + maintenance arrangements for the asset.",           needs: [] },
    ],
    clinq_service_key: "investor_introductions",
  },
  equity: {
    steps: [
      { text: "Tighten the deck: clear problem, defensible moat, named team CVs.",         needs: ["pitch_deck"] },
      { text: "Build a 5-year financial model with assumptions you can defend.",            needs: ["financial_model"] },
      { text: "Lock the cap table — founders should hold a credible majority (≥60%).",      needs: ["cap_table"] },
    ],
    clinq_service_key: "pitch_deck",
  },
  revenue_based_finance: {
    steps: [
      { text: "Upload 6-12 months of bank statements to prove recurring revenue.", needs: ["bank_statements"] },
      { text: "Show MRR / ARR trend with month-over-month growth.",                needs: ["mgmt_accounts"] },
      { text: "Document customer retention (cohort or churn data) — RBF underwriters care.", needs: [] },
    ],
    clinq_service_key: "financial_model",
  },
  grant: {
    steps: [
      { text: "Sharpen impact metrics — beneficiaries reached, SDG mapping, outcomes.", needs: ["impact_reports"] },
      { text: "Prepare an M&E framework (monitoring & evaluation plan).",                needs: ["impact_reports"] },
      { text: "Identify 3-5 grant windows aligned to your sector / geography.",          needs: [] },
    ],
    clinq_service_key: "investor_introductions",
  },
  blended_finance: {
    steps: [
      { text: "Quantify impact (SDG outcomes, beneficiary counts, climate metrics).",         needs: ["impact_reports"] },
      { text: "Upload audited financials so the commercial half of the blend can underwrite.", needs: ["audited_accounts"] },
      { text: "Draft a use-of-funds split: how much of the raise is grant vs. commercial.",    needs: [] },
    ],
    clinq_service_key: "investor_introductions",
  },
};


function _stepDone(step, uploadedDocTypes) {
  // A step counts as done iff EVERY doc_type it `needs` is in the uploaded set.
  // Steps that need nothing (free-form actions like "document fulfilment timeline")
  // are NOT auto-marked done — the founder still has to do them.
  if (!step.needs || step.needs.length === 0) return false;
  return step.needs.every((d) => uploadedDocTypes.has(d));
}


function CapitalUnlockModal({ open, capitalKey, reason, onClose, uploadedDocTypes = new Set() }) {
  if (!open) return null;
  const meta = CAPITAL_TYPE_PLAIN[capitalKey] || { title: capitalKey };
  const playbook = CAPITAL_TYPE_UNLOCK[capitalKey] || { steps: [] };
  const stepDone = playbook.steps.map((s) => _stepDone(s, uploadedDocTypes));
  const doneCount = stepDone.filter(Boolean).length;
  return (
    <div
      data-testid="capital-unlock-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
              How to unlock
            </p>
            <h3 className="mt-1 text-xl font-extrabold text-neutral-900">{meta.title}</h3>
            <p className="mt-1 text-xs leading-relaxed text-neutral-500">{meta.plain}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="capital-unlock-modal-close"
            aria-label="Close"
            className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {reason && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
            <b>Current blocker:</b> {reason}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#00828F]">
            Do these {playbook.steps.length} things to flip ❌ → ✅
          </p>
          <span
            data-testid="capital-unlock-progress"
            className={
              "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
              (doneCount === playbook.steps.length && playbook.steps.length > 0
                ? "bg-emerald-100 text-emerald-800"
                : doneCount > 0
                  ? "bg-amber-100 text-amber-800"
                  : "bg-neutral-200 text-neutral-700")
            }
          >
            {doneCount} / {playbook.steps.length} done
          </span>
        </div>
        <ol className="mt-2 space-y-2">
          {playbook.steps.map((s, i) => {
            const done = stepDone[i];
            return (
              <li
                key={`${i}-${(s.text || "").slice(0, 50)}`}
                data-testid={`capital-unlock-step-${i}`}
                data-done={done ? "1" : "0"}
                className={
                  "flex gap-2 rounded-lg px-3 py-2 text-sm leading-relaxed " +
                  (done
                    ? "bg-emerald-50 text-emerald-900 line-through decoration-emerald-400/60 decoration-2"
                    : "bg-neutral-50 text-neutral-800")
                }
              >
                {done ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                ) : (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#004B62] text-[11px] font-bold text-white">
                    {i + 1}
                  </span>
                )}
                <span>{s.text}</span>
              </li>
            );
          })}
        </ol>

        <div className="mt-6 flex flex-wrap gap-2">
          <LaunchPrepButton
            testid="capital-unlock-services-cta"
            prefillMissingDocs={["pitch_deck", "financial_model"].filter((d) => !uploadedDocTypes.has(d))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#D1A330] px-3 py-2 text-xs font-bold text-neutral-950 hover:bg-[#8A6A1E]"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Have Clinq Prep help me unlock this
          </LaunchPrepButton>
          <Link
            to="/onboarding?step=2"
            data-testid="capital-unlock-upload-cta"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#00828F] bg-white px-3 py-2 text-xs font-bold text-[#00647F] hover:bg-[#00828F]/5"
          >
            Upload the documents
          </Link>
        </div>
      </div>
    </div>
  );
}

export function CapitalStructurePanel({ recommended, capitalTypesViable, capitalTypesNotViable, uploadedDocTypes = new Set(), verdicts }) {
  const primary = recommended?.primary_type;
  const secondary = recommended?.secondary_type;
  const rationale = recommended?.rationale;
  // {key, reason} of the not-viable capital type the user wants to "unlock".
  const [unlockTarget, setUnlockTarget] = useState(null);
  if (!primary && !secondary && !rationale && (!capitalTypesViable || capitalTypesViable.length === 0)) return null;

  const primaryMeta = CAPITAL_TYPE_PLAIN[(primary || "").toLowerCase()] || { title: primary };
  const secondaryMeta = secondary ? CAPITAL_TYPE_PLAIN[secondary.toLowerCase()] || { title: secondary } : null;

  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
             data-testid="web-capital-structure">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        Best ways to raise capital
      </p>
      <h2 className="mt-1 text-2xl font-extrabold text-neutral-900">Recommended funding mix</h2>
      <p className="mt-2 text-sm leading-relaxed text-neutral-600">
        Based on your business, here&apos;s what we think you should chase first — and what to keep
        on the table as a backup.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {primary && (
          <div className="rounded-xl border-2 border-[#00828F] bg-[#00828F]/5 p-4"
               data-testid="web-capital-primary">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">
              Best fit — start here
            </p>
            <p className="mt-1 text-lg font-extrabold text-neutral-900">{primaryMeta.title}</p>
            {primaryMeta.plain && (
              <p className="mt-2 text-sm text-neutral-700">{primaryMeta.plain}</p>
            )}
          </div>
        )}
        {secondaryMeta && (
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4"
               data-testid="web-capital-secondary">
            <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">
              Secondary option
            </p>
            <p className="mt-1 text-lg font-extrabold text-neutral-900">{secondaryMeta.title}</p>
            {secondaryMeta.plain && (
              <p className="mt-2 text-sm text-neutral-600">{secondaryMeta.plain}</p>
            )}
          </div>
        )}
      </div>

      {rationale && (
        <div className="mt-5 rounded-lg bg-neutral-50 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Why we suggest this</p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-700">{rationale}</p>
        </div>
      )}

      {verdicts && Object.keys(verdicts).length > 0 && (
        <div className="mt-5" data-testid="capital-verdicts-table">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Capital Structure Assessment — every capital type, evaluated
          </p>
          <ul className="mt-2 divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200">
            {Object.values(verdicts).map((v) => (
              <li key={v.capital_type} data-testid={`capital-verdict-${v.capital_type}`}
                  className="flex flex-col gap-1.5 p-3 sm:flex-row sm:items-start sm:gap-3">
                <span className="shrink-0 text-sm font-bold text-neutral-900 sm:w-52">
                  {v.label}
                </span>
                <span className={"inline-flex w-fit shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide " +
                  (v.verdict === "VIABLE" ? "bg-emerald-100 text-emerald-800"
                    : v.verdict === "POSSIBLE" ? "bg-sky-100 text-sky-800"
                    : "bg-amber-100 text-amber-800")}>
                  {v.verdict}
                </span>
                <span className="text-xs leading-relaxed text-neutral-600">{v.rationale}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!verdicts && capitalTypesViable && capitalTypesViable.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            All viable options for you
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {capitalTypesViable.map((c) => {
              const meta = CAPITAL_TYPE_PLAIN[(c || "").toLowerCase()];
              return (
                <span key={c} title={meta?.plain || ""}
                      className="rounded-full bg-[#00828F]/10 px-3 py-1 text-[11px] font-bold text-[#00828F]">
                  {meta?.title || c.toString().replace(/_/g, " ")}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {!verdicts && capitalTypesNotViable && Object.keys(capitalTypesNotViable).length > 0 && (
        <div className="mt-5" data-testid="web-capital-not-viable">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Capital types that aren&apos;t a fit right now
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-neutral-500">
            Tap any row to see exactly what it takes to unlock that capital type.
          </p>
          <ul className="mt-2 space-y-1.5">
            {Object.entries(capitalTypesNotViable).map(([k, reason]) => {
              const key = (k || "").toLowerCase();
              const meta = CAPITAL_TYPE_PLAIN[key];
              const playbook = CAPITAL_TYPE_UNLOCK[key];
              const totalSteps = playbook?.steps?.length || 0;
              const doneSteps = (playbook?.steps || []).filter(
                (s) => _stepDone(s, uploadedDocTypes)
              ).length;
              const fullyUnlocked = totalSteps > 0 && doneSteps === totalSteps;
              return (
                <li key={k}>
                  <button
                    type="button"
                    onClick={() => setUnlockTarget({ key, reason })}
                    data-testid={`web-capital-not-viable-${k}`}
                    className="flex w-full items-start gap-2 rounded-lg border border-transparent bg-neutral-50 px-3 py-2 text-left text-xs text-neutral-700 transition-colors hover:border-[#00828F]/30 hover:bg-[#E5EEF1] focus:outline-none focus:ring-2 focus:ring-[#00828F]/40"
                  >
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-400" />
                    <span className="flex-1">
                      <b className="text-neutral-800">{meta?.title || k.replace(/_/g, " ")}:</b>{" "}
                      {reason || "Not aligned with your current stage."}
                    </span>
                    {totalSteps > 0 && (
                      <span
                        data-testid={`web-capital-progress-${k}`}
                        className={
                          "mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
                          (fullyUnlocked
                            ? "bg-emerald-100 text-emerald-800"
                            : doneSteps > 0
                              ? "bg-amber-100 text-amber-800"
                              : "bg-neutral-200 text-neutral-700")
                        }
                      >
                        {doneSteps}/{totalSteps} done
                      </span>
                    )}
                    <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#00828F]">
                      Unlock <ArrowRight className="h-3 w-3" />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <CapitalUnlockModal
        open={!!unlockTarget}
        capitalKey={unlockTarget?.key}
        reason={unlockTarget?.reason}
        onClose={() => setUnlockTarget(null)}
        uploadedDocTypes={uploadedDocTypes}
      />
    </section>
  );
}


/* ----------------------- 6. Funder shortlist ----------------- */
export function FunderShortlistPanel({ matchList, funderSources, exclusions }) {
  // Prefer the detailed funder_match_list (LLM output). Fallback to funder_sources (likelihood map).
  const items = useMemo(() => {
    if (Array.isArray(matchList) && matchList.length > 0) return matchList;
    return Array.isArray(funderSources) ? funderSources : [];
  }, [matchList, funderSources]);
  if (items.length === 0) return null;

  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
             data-testid="web-funder-shortlist">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        Who&apos;s most likely to back you
      </p>
      <h2 className="mt-1 text-2xl font-extrabold text-neutral-900">Funder shortlist</h2>
      <p className="mt-2 text-sm leading-relaxed text-neutral-600">
        We&apos;ve ranked the types of funders most likely to invest in a business
        like yours, with what they&apos;ll like and what they&apos;ll question.
      </p>
      {Array.isArray(exclusions) && exclusions.length > 0 && (
        <p className="mt-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs leading-relaxed text-neutral-600"
           data-testid="web-funder-exclusions">
          <b>Not on this list:</b> {exclusions.map((e) => `${e.funder_category} — ${e.reason}`).join(" · ")}
        </p>
      )}

      <ul className="mt-5 space-y-3" data-testid="web-funder-list">
        {items.map((m, i) => (
          <li key={`fs-${i}-${m.funder_category || m.id || i}`}
              className="rounded-xl border border-neutral-100 p-4 hover:border-[#00828F]/30">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="text-sm font-bold text-neutral-900">
                {m.funder_category || m.label}
              </p>
              {m.fit_score != null && (
                <span className="rounded-full bg-[#00828F]/10 px-2.5 py-0.5 text-xs font-bold text-[#00828F]">
                  Match {m.fit_score}/100
                </span>
              )}
              {m.likelihood && !m.fit_score && (
                <_LikelihoodBadge likelihood={m.likelihood}/>
              )}
            </div>
            {m.blurb && <p className="mt-1.5 text-xs text-neutral-600">{m.blurb}</p>}
            {(m.match_reasons || []).length > 0 && (
              <ul className="mt-2.5 space-y-1">
                {m.match_reasons.map((r, idx) => (
                  <li key={`mr-${i}-${idx}`} className="flex gap-2 text-xs text-neutral-700">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600"/>{r}
                  </li>
                ))}
              </ul>
            )}
            {(m.gaps || []).length > 0 && (
              <ul className="mt-1.5 space-y-1">
                {m.gaps.map((g, idx) => (
                  <li key={`gp-${i}-${idx}`} className="flex gap-2 text-xs text-neutral-700">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600"/>{g}
                  </li>
                ))}
              </ul>
            )}
            {m.rationale && !m.match_reasons && (
              <p className="mt-2 text-[11px] italic text-neutral-500">{m.rationale}</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function _LikelihoodBadge({ likelihood }) {
  const map = {
    high:   { bg: "bg-emerald-100", text: "text-emerald-800", label: "High match" },
    medium: { bg: "bg-amber-100",   text: "text-amber-800",   label: "Medium match" },
    low:    { bg: "bg-neutral-200", text: "text-neutral-700", label: "Low match" },
  };
  const m = map[likelihood] || map.medium;
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
}


/* ----------------------- 7. 90-day roadmap ----------------- */
export function RoadmapPanel({ roadmap }) {
  if (!Array.isArray(roadmap) || roadmap.length === 0) return null;
  const sorted = sortRoadmap(roadmap);
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
             data-testid="web-roadmap">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        Your 90-day game plan
      </p>
      <h2 className="mt-1 text-2xl font-extrabold text-neutral-900">What to do next</h2>
      <p className="mt-2 text-sm leading-relaxed text-neutral-600">
        {sorted.some((r) => Number.isInteger(r.closes_gap))
          ? "Each action closes a numbered gap from your Gaps & Weaknesses list — most blocking first. Tackle them in order."
          : "Tackle these in order. Each one directly pushes your score up and brings you closer to being funder-ready."}
      </p>
      <ol className="mt-5 space-y-3" data-testid="web-roadmap-list">
        {sorted.map((r, i) => {
          const pillar = r.pillar_addressed
            ? PILLAR_META.find((p) => p.key === r.pillar_addressed)
            : null;
          return (
            <li key={`rm-${i}`} className="flex gap-3 rounded-xl border border-neutral-100 p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#004B62] text-sm font-bold text-white">
                {i + 1}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {r.timeframe && (
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">
                      {r.timeframe}
                    </p>
                  )}
                  {Number.isInteger(r.closes_gap) && r.closes_gap > 0 && (
                    <span
                      data-testid={`web-roadmap-closes-gap-${i}`}
                      className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-600"
                    >
                      Closes gap {String(r.closes_gap).padStart(2, "0")}
                    </span>
                  )}
                  {pillar && (
                    <span
                      data-testid={`web-roadmap-pillar-${i}`}
                      className="rounded-full bg-[#00828F]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#00828F]"
                    >
                      Boosts {pillar.label}
                    </span>
                  )}
                </div>
                <p className="text-sm font-bold text-neutral-900">{r.milestone}</p>
                {r.capital_impact && (
                  <p className="mt-1 text-xs leading-relaxed text-neutral-600">
                    <span className="font-semibold text-neutral-700">Why it matters: </span>
                    {r.capital_impact}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}


/* ----------------------- 8. Methodology ----------------- */
export function MethodologyPanel({ funderTypeUsed }) {
  const ftLabel = FUNDER_TYPE_LABEL[funderTypeUsed] || "Seed / Pre-Series A VC";
  return (
    <section className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 sm:p-6"
             data-testid="web-methodology">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        How we calculated this
      </p>
      <h2 className="mt-1 text-xl font-extrabold text-neutral-900">A quick word on the score</h2>
      <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-neutral-700">
        <li className="flex gap-2.5">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#00828F]"/>
          <span>
            Your score (0–100) is a weighted average of six pillars. Higher = more ready to receive funding.
          </span>
        </li>
        <li className="flex gap-2.5">
          <Briefcase className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#00828F]"/>
          <span>
            We tailored the weights for the funder type you&apos;re targeting (<b>{ftLabel}</b>).
            Different funders care about different things.
          </span>
        </li>
        <li className="flex gap-2.5">
          <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#00828F]"/>
          <span>
            We read every document you uploaded — pitch deck, financials, legals, traction proof — to
            build the analysis. The more you upload, the more accurate the score.
          </span>
        </li>
        <li className="flex gap-2.5">
          <Globe className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#00828F]"/>
          <span>
            This is an <b>Indicative</b> score generated by AI. For a <b>Verified</b> score
            (hand-reviewed by a Clinq Expert), upgrade above — it materially improves
            your chances with institutional funders.
          </span>
        </li>
      </ul>
      <p className="mt-4 text-[11px] italic text-neutral-500">
        The Capital Readiness Score measures readiness — not creditworthiness, valuation, or
        guaranteed funding outcomes. Funders make their own decisions.
      </p>
      <p className="mt-3 text-[11px] italic text-neutral-500" data-testid="methodology-7b-note">
        Section 7B — Capital Range Assessment: ranges are estimates based on document inputs
        and East Africa market benchmarks. They are not offers, indicative terms, or guarantees
        of funding outcomes. Ranges are computed at report generation and will change as
        documents are updated or market conditions shift. USD conversion uses the exchange rate
        at the date of report generation. The Funder Version of this report does not include
        specific figures — full range detail is available to the founder in their Clinq portal.
      </p>
    </section>
  );
}


/* ----------------------- Composite (use this on report pages) ----------- */
export default function WebReportSections({ data, uploadedDocTypes, onSnapshotUpdate, editable }) {
  // `data` is the report payload (founder/readiness/report OR public-readiness-report).
  // `uploadedDocTypes` is an optional Set<string> of doc_types the founder has
  // already uploaded — drives the "X of Y unlocked" progress chips on the
  // Capital Unlock playbook. Only the authenticated route passes this; the
  // public share view leaves it undefined and the chips don't render.
  // `editable` + `onSnapshotUpdate` enable the inline "Edit details" button
  // on the Business Snapshot card — passed only from the founder-owned
  // /detailed-report view; public/share views render it read-only.
  const verifiedStatus = data?.verified_readiness?.status;
  const verifiedScore = data?.verified_readiness?.verified_score;
  const score = (verifiedStatus === "verified" && verifiedScore != null)
    ? verifiedScore
    : Number(data?.readiness_score || 0);
  const docTypeSet = uploadedDocTypes instanceof Set
    ? uploadedDocTypes
    : new Set(uploadedDocTypes || []);

  return (
    <div className="space-y-6">
      <BandPanel score={score} summary={data?.summary} contextLine={data?.score_context_line}/>
      <ExecutiveSummaryPanel data={data} score={score}/>
      <BusinessSnapshotPanel company={data?.company} editable={editable}
                              onSnapshotUpdate={onSnapshotUpdate} />
      <FinancialForecastPanel company={data?.company} />
      <PillarBreakdownPanel
        pillarScores={data?.pillar_scores}
        pillarWeights={data?.pillar_weights_used}
        funderTypeUsed={data?.funder_type_used}
        scoreBasis={data?.score_basis}
        pillarLabels={data?.pillar_labels}
        repayment={data?.repayment_capacity}
        totalScore={score}
        blendNote={data?.weights_blend_note}
      />
      <StrengthsWeaknessesPanel
        strengths={data?.strengths}
        weaknesses={data?.weaknesses}
        findings={data?.findings}
        score={score}
      />
      <CapitalStructurePanel
        recommended={data?.recommended_structure}
        capitalTypesViable={data?.capital_types_viable}
        capitalTypesNotViable={data?.capital_types_not_viable}
        uploadedDocTypes={docTypeSet}
        verdicts={data?.capital_verdicts?.verdicts}
      />
      <CapitalRangePanel range={data?.capital_range}/>
      <BorrowingCapacityPanel borrowing={data?.borrowing_capacity}/>
      <FunderShortlistPanel
        matchList={data?.funder_match_list}
        funderSources={data?.funder_sources}
        exclusions={data?.funder_match_exclusions}
      />
      <RoadmapPanel roadmap={data?.roadmap}/>
      <MethodologyPanel funderTypeUsed={data?.funder_type_used}/>
    </div>
  );
}


/* ----------------------- Section 7B — Recommended Capital Range --------- */
/* Tiered (UPPER/MID/LOWER) deterministic ranges — see core/capital_range.py */
const TIER_BADGE = {
  UPPER: "bg-emerald-100 text-emerald-800",
  MID: "bg-amber-100 text-amber-800",
  LOWER: "bg-neutral-200 text-neutral-700",
};

export function CapitalRangePanel({ range }) {
  if (!range || range.version !== "tiered_v2") return null;
  const entries = range.entries || [];
  const funderView = !!range.funder_version;
  const ask = range.ask || {};
  const okEntries = entries.filter((e) => e.status === "ok");
  const genDate = (range.computed_at || "").slice(0, 10);
  return (
    <section data-testid="capital-range-panel"
      className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0A8B85]/10">
          <Target className="h-5 w-5 text-[#0A8B85]"/>
        </div>
        <div>
          <h3 className="text-lg font-bold text-neutral-900">Capital Range Assessment</h3>
          <p className="text-xs text-neutral-500">Section 7B</p>
        </div>
      </div>
      <p className="mt-3 text-sm text-neutral-600">
        Based on your uploaded documents and Capital Readiness Score, here is how much you
        are likely to raise and in what format. Only capital types confirmed as viable in
        Section 7 are shown here.
      </p>

      {entries.length === 0 ? (
        <p className="mt-5 rounded-xl bg-neutral-50 p-4 text-sm text-neutral-600"
           data-testid="capital-range-insufficient">
          No capital types are currently viable for a range estimate. See Section 7 for the
          specific actions that will unlock each category.
        </p>
      ) : funderView ? (
        <div className="mt-5 space-y-2">
          {entries.map((e) => (
            <div key={e.capital_type} data-testid={`capital-range-entry-${e.capital_type}`}
              className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold text-neutral-900">{e.label}</p>
                <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800">Viable</span>
              </div>
              {e.constraint && <p className="mt-1 text-xs text-neutral-600">{e.constraint}</p>}
              <p className="mt-1 text-xs italic text-[#00676F]">Range available in the founder&apos;s Clinq portal.</p>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="mt-5 space-y-3">
            {entries.map((e) => (
              <div key={e.capital_type} data-testid={`capital-range-entry-${e.capital_type}`}
                className={"rounded-xl border p-5 " + (e.status === "ok"
                  ? "border-[#0A8B85]/25 bg-[#E0F3F5]" : "border-amber-200 bg-amber-50")}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-neutral-900">{e.label}</p>
                  {e.status === "ok" && (
                    <span data-testid={`capital-range-tier-${e.capital_type}`}
                      className={"rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide " +
                        (e.tier === "UPPER" ? "bg-emerald-100 text-emerald-800"
                          : e.tier === "MID" ? "bg-amber-100 text-amber-800"
                          : "bg-neutral-200 text-neutral-700")}>
                      {e.tier} tier
                    </span>
                  )}
                </div>
                {e.status === "ok" ? (
                  <>
                    <p className="mt-1.5 text-2xl font-extrabold text-neutral-900"
                       data-testid={`capital-range-value-${e.capital_type}`}>
                      {e.min_kes_fmt} – {e.max_kes_fmt}
                      <span className="ml-2 text-sm font-bold text-neutral-500">
                        ({e.min_usd_fmt} – {e.max_usd_fmt})
                      </span>
                    </p>
                    {e.pct_label && <p className="mt-0.5 text-xs text-neutral-600">{e.pct_label}</p>}
                    {e.constraint && (
                      <p className="mt-2 text-xs text-neutral-700" data-testid={`capital-range-constraint-${e.capital_type}`}>
                        {e.constraint}
                      </p>
                    )}
                    {e.upside && (
                      <p className="mt-2 rounded-lg bg-white/70 p-3 text-xs font-semibold text-[#00676F]"
                         data-testid={`capital-range-upside-${e.capital_type}`}>
                        {e.upside}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="mt-1 text-sm text-amber-900">{e.unlock_prompt}</p>
                )}
              </div>
            ))}
          </div>

          {["within", "above", "below"].includes(ask.flag) && (
            <div data-testid="capital-range-ask"
              className={"mt-4 rounded-xl border p-4 " + (ask.flag === "within"
                ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50")}>
              <p className={"text-[10px] font-bold uppercase tracking-widest " +
                (ask.flag === "within" ? "text-emerald-700" : "text-amber-700")}>
                Your stated ask · {ask.flag === "within" ? "Within range" : ask.flag === "above" ? "Above range" : "Below range"}
              </p>
              <p className="mt-1 text-xs text-neutral-600">{ask.note}</p>
            </div>
          )}

          {range.fx_usd_kes && (
            <p className="mt-4 text-[11px] text-neutral-500" data-testid="capital-range-fx-note">
              USD conversion rate: KES {Number(range.fx_usd_kes).toFixed(2)} as at {range.fx_date}.
            </p>
          )}
          <p className="mt-2 text-[11px] italic text-neutral-400" data-testid="capital-range-reeval-note">
            These ranges are based on your documents as at {genDate}. Upload new or updated
            documents and re-run your evaluation to see how your ranges change.
          </p>
        </>
      )}
    </section>
  );
}


/* ----------------------- Borrowing Capacity (Jun 2026) ----------------------- */
/* Deterministic 1.5x DSCR estimate from extracted EBITDA — see core/borrowing.py */
export function BorrowingCapacityPanel({ borrowing }) {
  if (!borrowing) return null;
  const state = borrowing.state;
  const cur = borrowing.currency;
  const basis = borrowing.basis_label || "EBITDA";
  // Money in the financials' own currency: USD keeps $, others prefix the ISO code.
  const money = (n) => {
    const s = _fmtUsd(n);
    return !cur || cur === "USD" || s === "—" ? s : s.replace("$", `${cur} `);
  };
  return (
    <section
      data-testid="borrowing-capacity-panel"
      className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8"
    >
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0A8B85]/10">
          <Landmark className="h-5 w-5 text-[#0A8B85]"/>
        </div>
        <div>
          <h3 className="text-lg font-bold text-neutral-900">What You Could Reasonably Borrow</h3>
          <p className="text-xs text-neutral-500">Based on a conservative 1.5× debt service coverage ratio (DSCR)</p>
        </div>
      </div>

      {state === "no_data" && (
        <p className="mt-5 rounded-xl bg-neutral-50 p-4 text-sm text-neutral-600" data-testid="borrowing-no-data">
          No EBITDA or net profit could be extracted from your uploads yet. Upload your financial model
          or management accounts showing profit and re-run the analysis to unlock your borrowing capacity estimate.
        </p>
      )}

      {state === "not_viable" && (
        <p className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="borrowing-not-viable">
          Your most recent {basis.toLowerCase()} ({borrowing.ebitda_year}) is not positive, so your cash flow
          can't support loan repayments at a 1.5× DSCR — debt isn't currently viable. Equity,
          grants or revenue-based finance are the appropriate path while you build profitable operations.
        </p>
      )}

      {state === "not_yet" && (
        <p className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="borrowing-not-yet">
          Withheld for now — debt is marked <b>NOT YET</b> in your capital structure assessment.{" "}
          {borrowing.verdict_reason || "Close the flagged gaps first."} An indicative loan figure will
          appear once debt moves to POSSIBLE or VIABLE.
        </p>
      )}

      {state === "fully_leveraged" && (
        <p className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="borrowing-fully-leveraged">
          Your existing debt service of {money(borrowing.existing_annual_debt_service)}/yr already absorbs the full
          1.5× DSCR capacity of {money(borrowing.max_annual_debt_service)}/yr on {basis.toLowerCase()} of {money(borrowing.ebitda)}.
          There&apos;s no headroom for a new facility until earnings grow or existing debt is repaid.
        </p>
      )}

      {state === "ok" && (
        <>
          <div className="mt-5 rounded-xl border border-[#0A8B85]/25 bg-[#E0F3F5] p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#066667]">
              Indicative maximum {borrowing.existing_annual_debt_service ? "new " : ""}loan
            </p>
            <p className="mt-1 text-3xl font-extrabold text-neutral-900" data-testid="borrowing-max-loan">
              ~{money(borrowing.max_loan)}
            </p>
            <p className="mt-1 text-xs text-neutral-600">
              Est. repayment {money(borrowing.est_monthly_repayment)}/month over {borrowing.tenor_years} years
            </p>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            {[
              [`${basis} (${borrowing.ebitda_year})`, money(borrowing.ebitda)],
              ...(borrowing.capacity_method === "blended"
                ? [[`Accounts capacity (${basis} ÷ 1.5)`, `${money(borrowing.accounts_annual_capacity)}/yr`],
                   [`Bank capacity (${borrowing.bank_months} mo · ${Math.round((borrowing.bank_turnover_ratio || 0.3) * 100)}% of avg inflows ${money(borrowing.bank_avg_monthly_inflow)}/mo)`, `${money(borrowing.bank_annual_capacity)}/yr`],
                   ["Blended capacity (50% bank · 50% accounts)", `${money(borrowing.max_annual_debt_service)}/yr`]]
                : []),
              ...(borrowing.existing_annual_debt_service
                ? [[`Existing debt service (${borrowing.existing_debt_source})`, `− ${money(borrowing.existing_annual_debt_service)}/yr`],
                   ["Available for a new facility", `${money(borrowing.residual_annual_debt_service)}/yr`]]
                : []),
              ...(borrowing.dscr_historical != null ? [["Historical DSCR", `${borrowing.dscr_historical.toFixed(2)}×`]] : []),
              ...(borrowing.dscr_after_new_loan != null
                ? [[`DSCR after requested ${money(borrowing.proposed_loan)} facility`, `${borrowing.dscr_after_new_loan.toFixed(2)}×`]] : []),
              ...(borrowing.capacity_method !== "blended" ? [[`Max annual debt service (${basis} ÷ 1.5)`, money(borrowing.max_annual_debt_service)]] : []),
              ["Assumed lending rate", `${Math.round(borrowing.annual_rate * 100)}% p.a.`],
              ["Assumed term", `${borrowing.tenor_years} years, monthly instalments`],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-2 rounded-lg bg-neutral-50 px-3 py-2">
                <span className="text-xs text-neutral-500">{k}</span>
                <span className="font-mono text-sm font-semibold text-neutral-800">{v}</span>
              </div>
            ))}
          </div>
          {borrowing.basis_note && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900" data-testid="borrowing-basis-note">
              {borrowing.basis_note}
            </p>
          )}
          <p className="mt-4 text-[11px] leading-relaxed text-neutral-400">
            Indicative only — actual facilities depend on lender diligence, security offered,
            sector risk and prevailing rates. A current KRA tax compliance certificate and
            audited/management accounts materially strengthen any debt application.
          </p>
        </>
      )}
    </section>
  );
}


/* ----------------------- Business Snapshot ----------------------- *//* Auto-extracted from the founder's uploaded pitch deck (Feb 2026 — replaces
 * the old Onboarding Step 1 form). Surfaces:
 *   • Sector of Company
 *   • Target capital raise ticket size
 *   • Target capital mix (equity / debt / grant / other %)
 */
function _fmtUsd(n) {
  if (n == null || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "—";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${v < 0 ? "-" : ""}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${v < 0 ? "-" : ""}$${(abs / 1_000).toFixed(0)}K`;
  return `${v < 0 ? "-" : ""}$${abs.toFixed(0)}`;
}


export function BusinessSnapshotPanel({ company, editable, onSnapshotUpdate }) {
  const [editOpen, setEditOpen] = useState(false);
  if (!company) return null;
  const tMin = Number(company.ticket_min || 0);
  const tMax = Number(company.ticket_max || 0);
  const ticket = (tMin > 0 || tMax > 0)
    ? `${_fmtUsd(tMin)} – ${_fmtUsd(tMax)}`
    : "—";
  const mix = company.capital_mix || null;
  const mixEntries = mix
    ? Object.entries(mix).filter(([, v]) => Number(v) > 0)
    : [];
  // AI confidence ribbon: surfaces overall extraction confidence + flags
  // any specific fields the LLM was unsure about so founders know what to
  // double-check before sharing the report with funders.
  const conf = company.extraction_confidence;
  const lowConf = Array.isArray(company.low_confidence_fields)
    ? company.low_confidence_fields
    : [];
  const showRibbon = (conf != null) || lowConf.length > 0;
  return (
    <section
      data-testid="web-business-snapshot"
      className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
          Business snapshot
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {showRibbon && (
            <ConfidenceRibbon
              confidence={conf}
              lowFields={lowConf}
              testid="biz-snap-confidence"
            />
          )}
          {editable && (
            <button
              type="button"
              data-testid="biz-snap-edit-btn"
              onClick={() => setEditOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-neutral-700 transition hover:border-[#00828F] hover:text-[#00828F]"
              title="Correct any auto-extracted business details"
            >
              <Pencil className="h-3 w-3" /> Edit details
            </button>
          )}
        </div>
      </div>
      <h2 className="mt-1 text-2xl font-extrabold text-neutral-900">
        Sector, ticket size &amp; capital mix
      </h2>
      <p className="mt-1 text-xs text-neutral-500">
        Auto-extracted from the documents you uploaded — keep your pitch deck
        in sync to refresh these on the next re-run.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <div data-testid="biz-snap-sector"
             className={"rounded-xl border bg-neutral-50 p-4 " + _lowConfClass("sector", lowConf)}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">Sector</p>
          <p className="mt-1.5 text-lg font-extrabold text-neutral-900">
            {company.sector || "—"}
          </p>
          {company.country && (
            <p className="mt-0.5 text-xs text-neutral-500">{company.country} · {company.stage || ""}</p>
          )}
        </div>
        <div data-testid="biz-snap-ticket"
             className={"rounded-xl border bg-neutral-50 p-4 " + _lowConfClass("ticket_max", lowConf)}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">Target raise</p>
          <p className="mt-1.5 text-lg font-extrabold text-neutral-900">{ticket}</p>
          <p className="mt-0.5 text-xs text-neutral-500">Ticket range</p>
        </div>
        <div data-testid="biz-snap-mix"
             className={"rounded-xl border bg-neutral-50 p-4 " + _lowConfClass("capital_mix", lowConf)}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#00828F]">Target capital mix</p>
          {mixEntries.length === 0 ? (
            <p className="mt-1.5 text-sm text-neutral-500">Not disclosed in the deck.</p>
          ) : (
            <ul className="mt-1.5 space-y-1">
              {mixEntries.map(([k, v]) => (
                <li key={k} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-neutral-700">{k}</span>
                  <span className="font-bold text-neutral-900">{v}%</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {editOpen && (
        <BusinessSnapshotEditModal
          company={company}
          lowFields={lowConf}
          onClose={() => setEditOpen(false)}
          onSaved={async () => { setEditOpen(false); await onSnapshotUpdate?.(); }}
        />
      )}
    </section>
  );
}


/* --- Composite edit modal for the Detailed Report Business Snapshot ---
 * Single-shot editor that lets founders correct sector, ticket range and
 * capital mix in one form (unlike the Dashboard's per-cell flow). Posts a
 * full SnapshotBody to /companies/me/snapshot, carrying forward every
 * auto-extracted field so we don't wipe other AI-derived values. Any of
 * the three groups that change get their `low_confidence_fields` entries
 * cleared so the AI Confidence ribbon updates on next load. */
const _BIZ_SECTOR_OPTIONS = [
  "Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics",
  "E-commerce", "Manufacturing", "Edtech", "Proptech", "Insurtech",
  "Hospitality", "Foodtech", "Real Estate", "Coworking", "Other",
];

function BusinessSnapshotEditModal({ company, lowFields, onClose, onSaved }) {
  const [sector, setSector] = useState(company.sector || "");
  const [tMin, setTMin] = useState(company.ticket_min ?? 0);
  const [tMax, setTMax] = useState(company.ticket_max ?? 0);
  const initialMix = company.capital_mix || { equity: 0, debt: 0, grant: 0, other: 0 };
  const [mix, setMix] = useState({
    equity: Number(initialMix.equity || 0),
    debt: Number(initialMix.debt || 0),
    grant: Number(initialMix.grant || 0),
    other: Number(initialMix.other || 0),
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const mixTotal = Object.values(mix).reduce((a, b) => a + Number(b || 0), 0);
  const mixOver = mixTotal > 100;

  const save = async () => {
    if (mixOver) { setErr("Capital mix totals more than 100%."); return; }
    setSaving(true); setErr("");
    try {
      const body = {
        // Required SnapshotBody fields — carry forward current values.
        company_name: company.company_name || "",
        website: company.website || "",
        sector: sector || company.sector || "",
        country: company.country || "",
        stage: company.stage || "",
        ticket_min: Number(tMin || 0),
        ticket_max: Number(tMax || 0),
        value_prop: company.value_prop || "",
        revenue_annual: Number(company.revenue_annual || 0),
        employees: Number(company.employees || 1),
        sdgs: company.sdgs || [],
        // Auto-extracted fields — preserve so we don't wipe them.
        legal_name: company.legal_name || null,
        registration_number: company.registration_number || null,
        director_name: company.director_name || null,
        revenue_fy2025: company.revenue_fy2025 ?? null,
        revenue_fy2026: company.revenue_fy2026 ?? null,
        ebitda_fy2025: company.ebitda_fy2025 ?? null,
        ebitda_fy2026: company.ebitda_fy2026 ?? null,
        // Apply the edited capital mix (zeroes are fine — backend tolerates).
        capital_mix: mix,
      };
      // Clear the edited groups from low_confidence_fields. We always clear
      // all three since this modal lets the founder review every cell at once.
      const cleared = new Set(["sector", "ticket_min", "ticket_max", "capital_mix"]);
      body.low_confidence_fields = (lowFields || []).filter((k) => !cleared.has(k));
      body.extraction_confidence = company.extraction_confidence ?? null;
      await api.post("/companies/me/snapshot", body);
      toast.success("Business details saved.");
      await onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="biz-snap-edit-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00828F]">
              Edit business snapshot
            </p>
            <h3 className="mt-1 text-xl font-extrabold text-neutral-900">
              Correct your AI-extracted details
            </h3>
            <p className="mt-1 text-xs text-neutral-500">
              These flow into your funder shortlist and Capital Readiness Score.
            </p>
          </div>
          <button
            type="button"
            data-testid="biz-snap-edit-close"
            onClick={onClose}
            className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500">
              Sector
            </label>
            <select
              data-testid="biz-snap-edit-sector"
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            >
              <option value="">Select…</option>
              {_BIZ_SECTOR_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              {company.sector && !_BIZ_SECTOR_OPTIONS.includes(company.sector) && (
                <option value={company.sector}>{company.sector} (current)</option>
              )}
            </select>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
              Target capital raise (USD)
            </p>
            <div className="mt-1 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Min</label>
                <input
                  data-testid="biz-snap-edit-ticket-min"
                  type="number" min="0" step="1000"
                  value={tMin}
                  onChange={(e) => setTMin(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Max</label>
                <input
                  data-testid="biz-snap-edit-ticket-max"
                  type="number" min="0" step="1000"
                  value={tMax}
                  onChange={(e) => setTMax(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
              Target capital mix
            </p>
            <div className="mt-1 space-y-2">
              {["equity", "debt", "grant", "other"].map((k) => (
                <div key={k} className="flex items-center gap-3">
                  <label className="w-20 text-xs font-semibold uppercase tracking-wider text-neutral-600 capitalize">{k}</label>
                  <input
                    data-testid={`biz-snap-edit-mix-${k}`}
                    type="number" min="0" max="100" step="1"
                    value={mix[k] ?? 0}
                    onChange={(e) => setMix({
                      ...mix,
                      [k]: Math.max(0, Math.min(100, Number(e.target.value || 0))),
                    })}
                    className="w-24 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm"
                  />
                  <span className="text-xs text-neutral-500">%</span>
                </div>
              ))}
              <p className="text-[11px] text-neutral-500">
                Total: {mixTotal}%
                {mixOver && <span className="ml-1 font-bold text-rose-600">— above 100%</span>}
              </p>
            </div>
          </div>
        </div>

        {err && <p className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            data-testid="biz-snap-edit-cancel"
            type="button"
            onClick={onClose}
            className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
          >Cancel</button>
          <button
            data-testid="biz-snap-edit-save"
            type="button"
            disabled={saving || mixOver}
            onClick={save}
            className="rounded-lg bg-[#004B62] px-4 py-2 text-sm font-bold text-white hover:bg-[#00303F] disabled:opacity-60"
          >{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}


/* Shared ribbon — overall confidence chip + (when present) a list of fields
 * the LLM flagged as guesses. Used by both BusinessSnapshotPanel and the
 * Founder dashboard snapshot card so the founder always sees the same trust
 * signal in both surfaces. */
/* Field-id → human label. Module-internal so other surfaces (e.g. the
 * Detailed Report rerun nudge) can render the same plain-English names. */
export const FIELD_LABEL = {
  sector: "Sector",
  country: "Country",
  stage: "Stage",
  ticket_min: "Ticket min",
  ticket_max: "Ticket size",
  revenue_annual: "Annual revenue",
  employees: "Employees",
  value_prop: "Value proposition",
  legal_name: "Legal name",
  registration_number: "Reg. number",
  director_name: "Director",
  revenue_fy2025: "FY2025 revenue",
  revenue_fy2026: "FY2026 revenue",
  ebitda_fy2025: "FY2025 EBITDA",
  ebitda_fy2026: "FY2026 EBITDA",
  capital_mix: "Capital mix",
};
const _FIELD_LABEL = FIELD_LABEL;


function _confTone(c) {
  if (c == null) return { wrap: "border-neutral-200 bg-neutral-50 text-neutral-600", dot: "bg-neutral-400" };
  if (c >= 80) return { wrap: "border-emerald-200 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" };
  if (c >= 60) return { wrap: "border-amber-200 bg-amber-50 text-amber-800", dot: "bg-amber-500" };
  return { wrap: "border-rose-200 bg-rose-50 text-rose-700", dot: "bg-rose-500" };
}


function _lowConfClass(field, lowFields) {
  return (lowFields || []).includes(field)
    ? "border-amber-300 ring-1 ring-amber-200"
    : "border-neutral-200";
}


export function ConfidenceRibbon({ confidence, lowFields, testid }) {
  const tone = _confTone(confidence);
  const labels = (lowFields || [])
    .map((k) => _FIELD_LABEL[k] || k)
    .slice(0, 4);
  return (
    <div
      data-testid={testid || "ai-confidence-ribbon"}
      title={lowFields?.length
        ? `Fields the AI was unsure about — review before sharing: ${labels.join(", ")}`
        : "AI was confident on every extracted field"}
      className={
        "inline-flex max-w-full items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider " +
        tone.wrap
      }
    >
      <span className={"h-1.5 w-1.5 shrink-0 rounded-full " + tone.dot} />
      <span>AI confidence {confidence != null ? `${confidence}%` : "—"}</span>
      {labels.length > 0 && (
        <span className="hidden truncate normal-case font-semibold tracking-normal sm:inline">
          · review: {labels.join(", ")}{(lowFields || []).length > labels.length ? "…" : ""}
        </span>
      )}
    </div>
  );
}


/* ----------------------- Financial Forecast ----------------------- */
/* Renders FY2025 + FY2026 revenue, EBITDA and computed EBITDA margin.
 * Hidden if both years are absent — image-only decks may not surface either.
 */
export function FinancialForecastPanel({ company }) {
  if (!company) return null;
  const rows = [
    { year: "FY2025", revenue: company.revenue_fy2025, ebitda: company.ebitda_fy2025 },
    { year: "FY2026", revenue: company.revenue_fy2026, ebitda: company.ebitda_fy2026 },
  ];
  const anyValue = rows.some((r) => r.revenue != null || r.ebitda != null);
  if (!anyValue) return null;
  const _pct = (e, r) => {
    if (e == null || r == null) return "—";
    const rn = Number(r);
    if (!Number.isFinite(rn) || rn === 0) return "—";
    return `${((Number(e) / rn) * 100).toFixed(1)}%`;
  };
  return (
    <section
      data-testid="web-financial-forecast"
      className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        Financial forecast
      </p>
      <h2 className="mt-1 text-2xl font-extrabold text-neutral-900">Revenue &amp; EBITDA outlook</h2>
      <p className="mt-1 text-xs text-neutral-500">
        Pulled from the financial model / forecast slide in your uploaded deck.
      </p>
      <div className="mt-5 overflow-x-auto rounded-xl border border-neutral-200">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="bg-neutral-50 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 text-left">Year</th>
              <th className="px-4 py-3 text-right">Revenue</th>
              <th className="px-4 py-3 text-right">Operating Profit / EBITDA</th>
              <th className="px-4 py-3 text-right">EBITDA margin</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.year} data-testid={`fcst-row-${r.year.toLowerCase()}`}
                  className="border-t border-neutral-100">
                <td className="px-4 py-3 font-bold text-neutral-900">{r.year}</td>
                <td className="px-4 py-3 text-right font-mono text-neutral-800">{_fmtUsd(r.revenue)}</td>
                <td className="px-4 py-3 text-right font-mono text-neutral-800">{_fmtUsd(r.ebitda)}</td>
                <td className="px-4 py-3 text-right font-mono text-neutral-800">{_pct(r.ebitda, r.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
